import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";

export default function NotesCMS() {
  const [notes, setNotes] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/notes").then(r => setNotes(r.data));
  useEffect(() => { load(); }, []);

  const empty = { title: "", body: "", reading_time: "5 min", published: false, slug: "" };

  const save = async () => {
    try {
      if (editing.resource_id) {
        await api.put(`/admin/notes/${editing.resource_id}`, editing);
      } else {
        await api.post("/admin/notes", editing);
      }
      toast.success("Saved.");
      setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed."); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/admin/notes/${id}`);
    toast.success("Deleted.");
    load();
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-6">
        <div className="text-xs text-zinc-500 font-mono-eyebrow uppercase tracking-[0.24em]">{notes.length} notes</div>
        <button data-testid="note-new" onClick={() => setEditing(empty)} className="rc-btn-gold text-[10px] inline-flex items-center gap-1"><Plus className="w-3 h-3" /> New Note</button>
      </div>
      <div className="space-y-3">
        {notes.map(n => (
          <div key={n.resource_id} data-testid={`note-row-${n.resource_id}`} className="hairline p-6 flex flex-wrap items-center gap-4 justify-between">
            <div>
              <div className="font-serif-editorial text-2xl">{n.title}</div>
              <div className="text-xs text-zinc-500 mt-1">{n.slug} · {n.reading_time}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-[10px] font-mono-eyebrow uppercase tracking-[0.24em] px-2 py-1 rounded-full border ${n.published ? "text-[#00E699] border-[#00E699]/40" : "text-zinc-500 border-zinc-700"}`}>{n.published ? "published" : "draft"}</span>
              <button data-testid={`note-edit-${n.resource_id}`} onClick={() => setEditing(n)} className="text-zinc-400 hover:text-white"><Edit3 className="w-4 h-4" /></button>
              <button data-testid={`note-delete-${n.resource_id}`} onClick={() => del(n.resource_id)} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div data-testid="note-editor" className="fixed inset-0 z-[55] bg-[#050507]/95 backdrop-blur-xl flex items-center justify-center p-6 overflow-y-auto">
          <div className="max-w-5xl w-full glass hairline p-8 relative my-8">
            <button onClick={() => setEditing(null)} className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            <div className="eyebrow-lg">{editing.resource_id ? "Edit Note" : "New Note"}</div>
            <div className="mt-6 grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="eyebrow-lg block mb-2">Title</label>
                  <input data-testid="note-title" value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} className="w-full px-4 py-3" />
                </div>
                <div>
                  <label className="eyebrow-lg block mb-2">Slug (optional)</label>
                  <input data-testid="note-slug" value={editing.slug || ""} onChange={e => setEditing({...editing, slug: e.target.value})} className="w-full px-4 py-3 font-mono-eyebrow text-xs" placeholder="auto-generated from title" />
                </div>
                <div>
                  <label className="eyebrow-lg block mb-2">Reading time</label>
                  <input data-testid="note-reading" value={editing.reading_time} onChange={e => setEditing({...editing, reading_time: e.target.value})} className="w-full px-4 py-3" />
                </div>
                <div>
                  <label className="eyebrow-lg block mb-2">Body · Markdown</label>
                  <textarea data-testid="note-body" rows={16} value={editing.body} onChange={e => setEditing({...editing, body: e.target.value})} className="w-full px-4 py-3 font-mono-eyebrow text-sm leading-relaxed" />
                </div>
                <label className="flex items-center gap-3 text-xs text-zinc-400 cursor-pointer">
                  <input data-testid="note-published" type="checkbox" checked={editing.published} onChange={e => setEditing({...editing, published: e.target.checked})} />
                  Published
                </label>
              </div>
              <div>
                <label className="eyebrow-lg block mb-2">Preview</label>
                <div className="hairline p-6 min-h-[400px]">
                  <h3 className="h-editorial text-3xl">{editing.title || "Untitled"}</h3>
                  <div className="mt-6 prose prose-invert prose-zinc max-w-none text-zinc-300 leading-relaxed [&_h2]:h-editorial [&_h2]:text-2xl [&_p]:mb-4">
                    <ReactMarkdown>{editing.body || "*Write in markdown…*"}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="rc-btn-ghost">Cancel</button>
              <button data-testid="note-save" onClick={save} className="rc-btn-primary">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
